import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type PendingUserEntityMemberInvitationJSON = z.infer<
  typeof PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationJSONZ
>;
export type PendingUserEntityMemberInvitationParams = z.infer<
  typeof PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationZ
>;

export default class PendingUserEntityMemberInvitationSchema {
  public static readonly PendingUserEntityMemberInvitationJSONZ = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    legalEntityId: z.uuid(),
    companyName: z.string().nullish(),
    email: z.email(),
    expires: z.iso.datetime({ offset: true }).nullish(),
    createClientRep: z.boolean().nullish(),
    createVendorRep: z.boolean().nullish(),
    isAdmin: z.boolean().nullish(),
  });

  public static readonly PendingUserEntityMemberInvitationZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    email: z.instanceof(EmailAddress),
    legalEntityId: z.instanceof(Guid),
    companyName: z.string().min(1),
    expires: DateSchema.type,
    createClientRep: z.boolean(),
    createVendorRep: z.boolean(),
    isAdmin: z.boolean(),
  });

  public static validate(values: unknown): PendingUserEntityMemberInvitationParams {
    return parseOrThrow(PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationZ, values, {
      logError: true,
      schemaName: "PendingUserEntityMemberInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): PendingUserEntityMemberInvitationJSON {
    return parseOrThrow(PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "PendingUserEntityMemberInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is PendingUserEntityMemberInvitationParams {
    try {
      PendingUserEntityMemberInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is PendingUserEntityMemberInvitationJSON {
    try {
      PendingUserEntityMemberInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): PendingUserEntityMemberInvitationParams {
    const parsed = parseOrThrow(PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationJSONZ, json, {
      logError: true,
      schemaName: "PendingUserEntityMemberInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return PendingUserEntityMemberInvitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: PendingUserEntityMemberInvitationParams): PendingUserEntityMemberInvitationJSON {
    const parsed = PendingUserEntityMemberInvitationSchema.validate(legalEntity);
    return PendingUserEntityMemberInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationJSONZ,
    PendingUserEntityMemberInvitationSchema.PendingUserEntityMemberInvitationZ,
    {
      decode: (json) => ({
        id: new Guid(GuidSchema.deserialize(json.id)),
        userId: new Guid(GuidSchema.deserialize(json.userId)),
        email: new EmailAddress(json.email),
        legalEntityId: new Guid(json.legalEntityId),
        companyName: z.string().min(1).parse(json.companyName),
        expires: new AHDate(DateSchema.deserialize(json.expires)),
        createClientRep: json.createClientRep ?? false,
        createVendorRep: json.createVendorRep ?? false,
        isAdmin: json.isAdmin ?? false,
      }),
      encode: (params) => {
        return {
          id: params.id.toJSON(),
          userId: params.userId.toJSON(),
          email: params.email.toJSON(),
          legalEntityId: params.legalEntityId.toJSON(),
          expires: params.expires.toJSON(),
          companyName: params.companyName,
          createClientRep: params.createClientRep,
          createVendorRep: params.createVendorRep,
          isAdmin: params.isAdmin,
        };
      },
    },
  );
}
